import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DataProvider } from "projects/packcert-web/src/app/providers/data.provider";
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { AdminContextService } from '../../context/admin-contex.service';
import { AdminService } from '../../services/admin.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';

@Component({
    selector: 'app-mantenimiento-municipios',
    templateUrl: './mantenimiento-municipios.component.html'
})
export class MantenimientoMunicipiosComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;

    private _filters;
    public filtersVisible = false;
    public filtersForm: UntypedFormGroup;

    public municipios;
    public provincias;
    public municipiosFiltro;

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public deleteModal = false;
    public newEditModal = false;

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 0;
    public totalItems: number;
    public readonly visiblePages = 3;

    public toEditId;
    public toDeleteId;

    public disabledProvincias: Subject<boolean> = new Subject();
    public disabledMunicipios: Subject<boolean> = new Subject();

    public orderBy: string;
    public columnId: number;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _spinnerService: SpinnerService,
        private readonly _sessionContext: SessionContextService,
        private readonly _adminContext: AdminContextService,
        private readonly _adminService: AdminService,
        private readonly _dataProvider: DataProvider,
        private readonly utilsService: UtilsService,
        private readonly _translate: TranslateService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            pais: ['', Validators.required],
            provincia: [{ value: '', disabled: true }],
            municipio: [{ value: '', disabled: true }],
            fechaInicio: [''],
            fechaFin: [''],
            dateType: ['false'],
        });

        this._adminContext.mantenimientoMunicipiosFilters = null;

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());

        this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this._filters = {
                    paisId: pais.id
                }
                this._spinnerService.showSpinner();
                this._adminService.getProvinciasByPaisId(this._filters).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    if (resp.lista.length > 0) {
                        this.provincias = resp.lista;
                        this.disabledProvincias.next(false);
                        this.filtersForm.get('provincia').enable();
                    }
                });
            } else {
                this.provincias = [];
                this.disabledProvincias.next(true);
                this.filtersForm.get('provincia').patchValue('');
                this.filtersForm.get('provincia').disable();
            }
        });

        this.filtersForm.get('provincia').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((provincia) => {
            if (provincia) {
                this._filters = {
                    provinciaId: provincia.id
                };
                this._spinnerService.showSpinner();
                this._adminService.getMunicipiosByProvinciaId(this._filters).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    if (resp.lista.length > 0) {
                        this.municipiosFiltro = resp.lista;
                        this.disabledMunicipios.next(false);
                        this.filtersForm.get('municipio').enable();
                    }
                });
            } else {
                this.municipiosFiltro = [];
                this.disabledMunicipios.next(true);
                this.filtersForm.get('municipio').patchValue('');
                this.filtersForm.get('municipio').disable();
            }
        });

        this._adminContext.mantenimientoMunicipiosFilters = null;

        this.checkWarnings(this.filtersForm.get('pais').value);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get paises() {
        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.filtersForm.get('pais').enabled) {
              this.filtersForm.get('pais').patchValue(this._dataProvider.paises[0]);
              this.filtersForm.get('pais').disable();
            }
          }
        return this._dataProvider.paises;
    }

    public get defaultLanguage() {
      return this._translate.getDefaultLang();
    }

    public get isSmallScreen(){
      return this._sessionContext.isSmallScreen();
    }

    private checkWarnings(pais) {
        if (pais && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getMunicipios();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
        }
    }

    public pageChange(page) {
        this._adminContext.mantenimientoMunicipiosFilters.numPagina = this.currentPage = page - 1;
        this.getMunicipios();
    }

    public itemsPerPageChange(items) {
        this._adminContext.mantenimientoMunicipiosFilters.numPagina = this.currentPage = 0;
        this._adminContext.mantenimientoMunicipiosFilters.tamPagina = this.itemsPerPage = items;
        this.getMunicipios();
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').value) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').value) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio'});
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public checkValuesForm(): boolean {
        return ((this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
                this.filtersForm.get('provincia').value ||
                this.filtersForm.get('municipio').value ||
                this.filtersForm.get('fechaInicio').value ||
                this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public filtrar() {
        this.validDates();
        if (this.filtersForm.valid) {
            this.applyFilters();
        } else {
            if (this.filtersForm.get('pais').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
        }
    }

    public resetForm() {
        this.filtersForm.reset({
            pais: this.filtersForm.get('pais').disabled ? this.filtersForm.get('pais').value : null
        });
        this.filtersForm.get('dateType').patchValue('false');
    }

    public applyFilters() {
        this.currentPage = 0;
        this._adminContext.mantenimientoMunicipiosFilters = {
            paisId: this.filtersForm.get('pais').value ? this.filtersForm.get('pais').value.id : '',
            provinciaId: this.filtersForm.get('provincia').value ? this.filtersForm.get('provincia').value.id : '',
            municipioId: this.filtersForm.get('municipio').value ? this.filtersForm.get('municipio').value.id : '',
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this.utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
            fechaActualizacion: this.filtersForm.get('dateType').value,
            numPagina: this.currentPage,
            tamPagina: this.itemsPerPage
        };
        this.getMunicipios();
    }

    public getMunicipios() {
        if (this._adminContext.mantenimientoMunicipiosFilters) {
            this._filters = this._adminContext.mantenimientoMunicipiosFilters;
        } else {
            this.applyFilters();
        }
        this._spinnerService.showSpinner();
        this._adminService.getMunicipios(this._filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.totalItems = resp.paginacionMeta.totalCount;
            this.municipios = resp.lista;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    public create() {
        this.toEditId = null;
        this.newEditModal = true;
    }

    public update(municipio) {
        this.toEditId = municipio.id;
        this.newEditModal = true;
    }

    public askDelete(municipio) {
        this.toDeleteId = municipio.id;
        this.deleteModal = true;
    }

    public delete() {
        this._spinnerService.showSpinner();
        this._adminService.deleteMunicipio(this.toDeleteId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then(() => {
            this.close();
            this.pushMessage('global.success.itemDeleted');
            this.getMunicipios();
        });
    }

    public close() {
        this.deleteModal = false;
        this.newEditModal = false;
    }

    public refresh(value) {
        this.newEditModal = false;
        if (value) {
            this.getMunicipios();
        }
    }

    public pushMessage(mensaje) {
        this.showError = true;
        this.mensajes.push({ class: 'success', message: mensaje });
    }

    public trackByMunicipioId(index: number, municipio: { id: number }): number {
        return municipio.id;
    }

    public getIcon(prop: string) {
      let iconClass = 'fa fa-sort';
      if (this._filters && this._filters.orderItems && this._filters.orderItems[0].orderProperty === prop) {
          iconClass = this._filters.orderItems[0].orderByDescending === true ? 'fa fa-sort-down' : 'fa fa-sort-up';
      }
      return iconClass;
    }

    public toOrderBy(columnFilter, idColumn) {
      this.columnId = idColumn;
      if (this.municipios && this.municipios.length > 0) {
        if (this._adminContext.mantenimientoMunicipiosFilters) {
          this._filters = this._adminContext.mantenimientoMunicipiosFilters;
          if (!this.orderBy || this.orderBy.includes('DESC')) {
            this.orderBy = 'ASC' + idColumn;
            this._filters.orderItems = [
              {
                orderProperty: columnFilter,
                orderByDescending: false
              }
            ];
          } else if (this.orderBy.includes('ASC')) {
            this.orderBy = 'DESC' + idColumn;
            this._filters.orderItems = [
              {
                orderProperty: columnFilter,
                orderByDescending: true
              }
            ];
          }
          this.getMunicipios();
        }
      }
    }
}
